import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Inventory2, Category, LocalShipping } from '@mui/icons-material';
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
  type Variants,
} from 'framer-motion';
import { tokens } from '../../theme/tokens';

export type CatMood = 'idle' | 'hover' | 'loading' | 'error' | 'success';

const EASE = [0.16, 1, 0.3, 1] as const;
const ROTATE_MS = 4200;

const FEATURES = [
  { label: 'Inventory', Icon: Inventory2, line: 'Know exactly what is on every shelf, as it changes.' },
  { label: 'Bundles', Icon: Category, line: 'Build, price and retire bundles without the spreadsheets.' },
  { label: 'Carding', Icon: LocalShipping, line: 'Move stock and packaging together, with nothing missing.' },
];

const TAPE_PHRASES = ['HANDLE WITH INSTINCT', 'WHICH LIFE ARE WE WEARING TODAY?'];

// Fine film grain so the flat orange reads as printed, not as a screen gradient.
const GRAIN =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.55 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";

const stack: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.15 } },
};
const rise: Variants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

// The cat reacts to what the person is doing on the sign-in side: it leans in when they reach
// for a button, bounces while a request is in flight, shakes its head on an error and hops on
// success. Transform-only, so it never causes layout work.
const catVariants: Variants = {
  idle: {
    opacity: 1,
    y: [0, -12, 0],
    x: 0,
    scaleX: 1,
    scaleY: 1,
    rotate: 0,
    transition: { y: { duration: 6, ease: 'easeInOut', repeat: Infinity } },
  },
  hover: {
    opacity: 1,
    y: -8,
    x: 0,
    scaleX: 1.06,
    scaleY: 0.96,
    rotate: -2.5,
    transition: { type: 'spring', stiffness: 260, damping: 14 },
  },
  loading: {
    opacity: 1,
    y: [0, -14, 0],
    x: 0,
    scaleX: [1, 0.97, 1],
    scaleY: [1, 1.04, 1],
    rotate: 0,
    transition: { duration: 0.7, ease: 'easeInOut', repeat: Infinity },
  },
  error: {
    opacity: 1,
    y: 0,
    x: [0, -12, 12, -8, 8, 0],
    scaleX: 1,
    scaleY: 1,
    rotate: [0, -3, 3, -2, 2, 0],
    transition: { duration: 0.55, ease: 'easeOut' },
  },
  success: {
    opacity: 1,
    y: [0, -56, 0],
    x: 0,
    scaleX: [1, 0.94, 1.08, 1],
    scaleY: [1, 1.1, 0.9, 1],
    rotate: 0,
    transition: { duration: 0.75, ease: 'easeOut' },
  },
};

const TapeRun: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
    {TAPE_PHRASES.map((phrase) => (
      <Box key={phrase} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.18em',
            color: tokens.ink,
            whiteSpace: 'nowrap',
          }}
        >
          {phrase}
        </Typography>
        <Box
          component="img"
          src="/favicon-light-512.png"
          alt=""
          aria-hidden
          sx={{ height: 16, mx: 3, display: 'block' }}
        />
      </Box>
    ))}
  </Box>
);

const BrandPanel: React.FC<{ mood?: CatMood }> = ({ mood = 'idle' }) => {
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);

  // Rotate the feature spotlight. Reduced motion keeps the first item and skips the timer;
  // `active` is in the deps so choosing a pill restarts the countdown.
  useEffect(() => {
    if (reduceMotion) return undefined;
    const id = window.setTimeout(() => setActive((i) => (i + 1) % FEATURES.length), ROTATE_MS);
    return () => window.clearTimeout(id);
  }, [active, reduceMotion]);

  // Pointer parallax for the cat — spring-smoothed and capped so it feels alive without
  // competing with the form.
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, { stiffness: 90, damping: 18, mass: 0.6 });
  const sy = useSpring(py, { stiffness: 90, damping: 18, mass: 0.6 });
  const catX = useTransform(sx, (v) => v * 18);
  const catY = useTransform(sy, (v) => v * 14);
  const catRotate = useTransform(sx, (v) => v * 2.5);

  const onMove = (e: React.PointerEvent<HTMLElement>) => {
    if (reduceMotion) return;
    const r = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    px.set(0);
    py.set(0);
  };

  return (
    <Box
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        width: { xs: '100%', md: '46%', lg: '48%' },
        minHeight: { xs: 168, md: '100vh' },
        color: tokens.surface,
        backgroundColor: tokens.orange,
        backgroundImage: [
          'radial-gradient(75% 60% at 10% 0%, rgba(255,150,100,0.5) 0%, rgba(255,150,100,0) 70%)',
          'radial-gradient(85% 70% at 100% 100%, rgba(184,69,30,0.55) 0%, rgba(184,69,30,0) 72%)',
        ].join(','),
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: GRAIN,
          opacity: 0.1,
          mixBlendMode: 'multiply',
          pointerEvents: 'none',
        }}
      />

      {/* Soft stage ring behind the cat — echoes the balloon motif. */}
      <Box
        aria-hidden
        component={motion.div}
        initial={reduceMotion ? false : { opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.1, ease: EASE }}
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'absolute',
          width: { md: 500, lg: 620 },
          height: { md: 500, lg: 620 },
          borderRadius: '50%',
          top: '44%',
          left: '50%',
          transform: 'translate(-50%, -58%)',
          background:
            'radial-gradient(closest-side, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.07) 62%, rgba(255,255,255,0) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Cream lockup — cream-on-orange is an approved brand pairing. */}
      <Box
        component={motion.div}
        initial={reduceMotion ? false : { opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        sx={{
          position: 'relative',
          zIndex: 2,
          px: { xs: 3, md: 7, lg: 9 },
          pt: { xs: 2.5, md: 6 },
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src="/logo/salty-logo-cream.svg"
          alt="Salty"
          sx={{
            height: { xs: 96, md: 132 },
            width: 'auto',
            display: 'block',
            ml: { xs: -3, md: -5 },
            mt: { xs: -3, md: -5 },
            mb: { xs: -3, md: -5 },
          }}
        />
      </Box>

      {/* Hero cat — solid black, as the guideline requires for the logomark. */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          position: 'relative',
          zIndex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          px: 6,
          pb: 2,
        }}
      >
        <motion.div
          style={{
            x: reduceMotion ? 0 : catX,
            y: reduceMotion ? 0 : catY,
            rotate: reduceMotion ? 0 : catRotate,
          }}
        >
          <motion.img
            src="/favicon-light-512.png"
            alt=""
            aria-hidden
            variants={catVariants}
            initial={reduceMotion ? false : { opacity: 0, y: 28, scaleX: 0.9, scaleY: 0.9 }}
            animate={reduceMotion ? undefined : mood}
            transition={{ opacity: { duration: 0.6, delay: 0.2 } }}
            style={{
              display: 'block',
              width: 'min(32vw, 440px)',
              height: 'auto',
              transformOrigin: '50% 100%',
              filter: 'drop-shadow(0 28px 38px rgba(90,26,6,0.38))',
            }}
          />
        </motion.div>
      </Box>

      {/* Headline + rotating spotlight */}
      <Box
        component={motion.div}
        variants={stack}
        initial={reduceMotion ? false : 'hidden'}
        animate="show"
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'relative',
          zIndex: 2,
          px: { md: 7, lg: 9 },
          pb: 4.5,
        }}
      >
        <Typography
          component={motion.h2}
          variants={rise}
          sx={{
            fontWeight: 700,
            color: tokens.beige,
            fontSize: { md: '3rem', lg: '4.25rem' },
            lineHeight: 1.02,
            letterSpacing: '-0.04em',
            mb: 2.5,
          }}
        >
          Let&apos;s move
          <br />
          some boxes.
        </Typography>

        <Box component={motion.div} variants={rise} sx={{ minHeight: 56, maxWidth: 400, mb: 3 }}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.p
              key={active}
              initial={reduceMotion ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -10 }}
              transition={{ duration: 0.28, ease: EASE }}
              aria-live="off"
              style={{
                margin: 0,
                color: tokens.ink,
                fontSize: '1.05rem',
                lineHeight: 1.55,
              }}
            >
              {FEATURES[active].line}
            </motion.p>
          </AnimatePresence>
        </Box>

        <Box component={motion.div} variants={rise} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {FEATURES.map(({ label, Icon }, i) => {
            const on = i === active;
            return (
              <Box
                key={label}
                component="button"
                type="button"
                aria-pressed={on}
                onClick={() => setActive(i)}
                sx={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.75,
                  py: 0.9,
                  minHeight: 36,
                  border: 0,
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  letterSpacing: '0.02em',
                  color: tokens.ink,
                  bgcolor: 'rgba(26,20,15,0.14)',
                  overflow: 'hidden',
                  transition: 'background-color 0.2s',
                  '&:hover': { bgcolor: 'rgba(26,20,15,0.22)' },
                  '&:focus-visible': { outline: `2px solid ${tokens.beige}`, outlineOffset: 2 },
                }}
              >
                {on && (
                  <motion.span
                    layoutId="feature-pill"
                    transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: 999,
                      background: tokens.beige,
                    }}
                  />
                )}
                <Icon sx={{ fontSize: 16, position: 'relative' }} />
                <span style={{ position: 'relative' }}>{label}</span>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Brand tape — straight along the bottom edge, per the guideline. Static: the page
          already has two moving things (the cat and the spotlight). */}
      <Box
        aria-hidden
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'relative',
          zIndex: 2,
          alignItems: 'center',
          height: 40,
          bgcolor: tokens.beige,
          overflow: 'hidden',
          borderTop: '1px solid rgba(26,20,15,0.12)',
        }}
      >
        <Box sx={{ display: 'flex', width: 'max-content', ml: -4 }}>
          {[0, 1, 2].map((i) => (
            <TapeRun key={i} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default BrandPanel;
